import { Box, Button, Checkbox, Group, InputLabel } from "@mantine/core"
import { ListTableActionIconGroup } from "../../-components/list-table-action-icon-group"
import { useUserLocalStorage } from "../../-components/use-user-local-storage"
import { Page } from "../../../../-components/Page"
import { ResourceTypeCategory } from "../../../../@client/Theasoft_DataClasses_Dispo"
import { ResourceObjectsCardList } from "./ResourceObjectsCardList"
import { ResourceObjectsTable } from "./ResourceObjectsTable"
import { ResourceTypeCategorySelect } from "./ResourceTypeCategorySelect"
import { useNavigate, useSearch, useLocation } from "@tanstack/react-router"
import { SortingState } from "@tanstack/react-table"
import { ResourceObjectsSegmentControl } from "./ResourceObjectsSegmentControl"
import {
  getPaginatedResourceObjects,
  ResourceObjectDto,
  useGetResourceFieldTypes,
  useGetResourcePermission,
  useGetResourceTypeCategoriesSuspense,
  useGetSimpleOwnerGroupsSuspense,
} from "@/orval/gen"
import {
  FieldFilterButton,
  FieldFilterPill,
  AddFieldFilterModal,
  getUpdatedFilterDict,
} from "@components/field-data"
import { useDisclosure } from "@mantine/hooks"
import { useTranslation } from "react-i18next"
import {
  ResponsiveFilterGroup,
  useResponsiveFilterQueryOptions,
} from "../../-components/hideable-filter-group"
import { SearchTextInput } from "../../-components/search-text-input"
import { ResourceTypeTreeSelect } from "./ResourceTypeTreeSelect"
import { useDefaultInfiniteQuery } from "@/utils/useDefaultInfiniteQuery"
import { SortingStateAndUpdater } from "@/routes/-components/custom-tanstack-table"
import { ErrorAlert } from "@/blocks/errors/error-alert"
import { CreateResourceObjectsModal } from "./CreateResourceObjectsModal"

export function ResourceObjectsPage() {
  interface Search {
    category?: ResourceTypeCategory
    sorting?: SortingState
    globalFilter?: string
    resourceTypeTreeNodes?: string[]
    available?: boolean
    availableCount?: number
    fieldFilter?: Record<number, any>
  }

  const searchRaw = useSearch({ from: "/app/resources/resource-objects" })
  const search = searchRaw as Search

  const [view, setView] = useUserLocalStorage<"table" | "list">({
    key: "resources-page-view",
    defaultValue: "list",
  })
  const { data: ownerGroups } = useGetSimpleOwnerGroupsSuspense()
  const defaultOwnerGroupId = ownerGroups?.[0]?.ownerGroupId ?? 0

  const { data: permission } = useGetResourcePermission({
    ownerGroupId: defaultOwnerGroupId,
  })
  const canWriteResources = permission === "Write"

  const { data: categories } = useGetResourceTypeCategoriesSuspense()

  const [storedCategory, setStoredCategory] =
    useUserLocalStorage<ResourceTypeCategory>({
      key: "resource-category",
      defaultValue: categories[0],
    })
  React.useEffect(() => {
    if (search.category && storedCategory !== search.category) {
      setStoredCategory(search.category)
    }
  }, [search.category, storedCategory, setStoredCategory])
  const navigate = useNavigate()
  const navigateToResources = () =>
    navigate({
      to: "/app/resources/resources",
      search: { availableCount: 1 },
    })

  const { data: fieldTypes } = useGetResourceFieldTypes(
    {
      category: search.category ?? categories[0],
      fieldVisibility: "Resource",
      previewOnly: true,
    },
    { query: { initialData: [] } }
  )
  const { data: objectFieldTypes } = useGetResourceFieldTypes(
    {
      category: search.category ?? categories[0],
      fieldVisibility: "ResourceObject",
      previewOnly: true,
    },
    { query: { initialData: [] } }
  )
  const mergedFieldTypes = [...fieldTypes, ...objectFieldTypes]

  const [visible, setVisible] = useUserLocalStorage<boolean>({
    key: "resources-filter-visibility",
    defaultValue: true,
  })
  const queryOptions = useResponsiveFilterQueryOptions(visible)

  const [modalOpened, { open, close }] = useDisclosure()
  const { t } = useTranslation("app_server")

  const location = useLocation().pathname

  const isListingPath =
    location === "/app/resources/resource-objects" ||
    location === "/app/resources/resource-objects/"

  const updateSearch = (newValue: Partial<typeof search>) => {
    if (!isListingPath) return
    const merged = {
      ...search,
      ...newValue,
      available: ((newValue as any).available ??
        search.available ??
        true) as boolean,
    }

    navigate({
      to: "/app/resources/resource-objects",
      search: merged as any,
      replace: true,
    })
  }

  const onClickItem = (item: ResourceObjectDto) => {
    navigate({
      to: "/app/resources/resource-objects/$id",
      params: { id: item.resourceObjectId.toString() },
    })
  }

  const [createModalOpened, { open: openCreate, close: closeCreate }] =
    useDisclosure()

  const [
    createObjectsModalOpened,
    { open: openCreateObjects, close: closeCreateObjects },
  ] = useDisclosure()

  const queryResult = useDefaultInfiniteQuery(
    getPaginatedResourceObjects,
    (pageRequest) => ({
      ...pageRequest,
      ...search,
    }),
    queryOptions
  )

  const sortingState: SortingStateAndUpdater = [
    search.sorting ?? [],
    (updater) => {
      const newValue =
        typeof updater === "function" ? updater(search.sorting ?? []) : updater
      updateSearch({ sorting: newValue })
    },
  ]

  const category = search.category

  React.useEffect(() => {
    if (category) return
    if (!isListingPath) return

    if (storedCategory) {
      navigate({
        to: "/app/resources/resource-objects",
        search: {
          ...search,
          category: storedCategory,
          available: search.available ?? true,
        } as any,
        replace: true,
      })
      return
    }

    if (categories.length === 0) return

    navigate({
      to: "/app/resources/resource-objects",
      search: {
        ...search,
        category: categories[0],
        available: search.available ?? true,
      } as any,
      replace: true,
    })
  }, [category, storedCategory, categories, isListingPath, navigate])

  if (!category) {
    if (categories.length === 0) return <ErrorAlert error={t("NothingFound")} />
    return null
  }

  return (
    <Page
      crumbOverride={() => (
        <Group>
          <ResourceTypeCategorySelect
            value={category}
            onChange={(value) => updateSearch({ category: value })}
          />
          <ResourceObjectsSegmentControl
            category={category}
            value={"Objects"}
            onChange={navigateToResources}
          />
        </Group>
      )}
      buttonGroupProps={{
        children: (
          <Group>
            {canWriteResources && (
              <Button onClick={openCreateObjects}>{t("New")}</Button>
            )}
            <ListTableActionIconGroup
              value={view}
              onChange={(value) => {
                setView(value)
              }}
            />
          </Group>
        ),
      }}
      titleGroupProps={{ align: "start" }}
    >
      <Group pb={5} justify="space-between">
        <Group>
          <SearchTextInput
            autoFocus
            value={search.globalFilter ?? undefined}
            onChange={(e) => updateSearch({ globalFilter: e.target.value })}
          />
          <ResponsiveFilterGroup
            align="end"
            visible={visible}
            setVisible={setVisible}
            filters={{
              resourceTypes: search.resourceTypeTreeNodes,
              available: search.available,
              ...Object.fromEntries(
                Object.entries(search.fieldFilter ?? {}).map(([id, filter]) => [
                  `field-${id}`,
                  filter,
                ])
              ),
            }}
            onReset={() =>
              updateSearch({
                resourceTypeTreeNodes: undefined,
                available: false,
                fieldFilter: undefined,
              })
            }
          >
            <ResourceTypeTreeSelect
              category={category}
              label={t("Type")}
              miw={250}
              onCheckedStateChange={(values) =>
                updateSearch({ resourceTypeTreeNodes: values })
              }
              initialCheckedState={search.resourceTypeTreeNodes ?? []}
            />
            <Box>
              <InputLabel>{t("Available")}</InputLabel>
              <Checkbox
                size="md"
                p={5}
                checked={search.available}
                onChange={() => updateSearch({ available: !search.available })}
              />
            </Box>
            {mergedFieldTypes.length > 0 && (
              <FieldFilterButton onClick={open} />
            )}
            {mergedFieldTypes.map(
              (type) =>
                !!search.fieldFilter?.[type.id] && (
                  <FieldFilterPill
                    typeName={type.name}
                    filter={search.fieldFilter[type.id]}
                    onRemove={() => {
                      if (search.fieldFilter) {
                        const { [type.id]: _, ...rest } = search.fieldFilter

                        updateSearch({ fieldFilter: rest })
                      }
                    }}
                  />
                )
            )}
          </ResponsiveFilterGroup>
        </Group>
        <ResponsiveFilterGroup.Button
          visible={visible}
          onChange={setVisible}
          filters={{
            resourceTypes: search.resourceTypeTreeNodes,
            available: search.available,
            ...Object.fromEntries(
              Object.entries(search.fieldFilter ?? {}).map(([id, filter]) => [
                `field-${id}`,
                filter,
              ])
            ),
          }}
        />
      </Group>
      {view === "list" ? (
        <ResourceObjectsCardList
          queryResult={queryResult}
          onClickItem={onClickItem}
        />
      ) : (
        <ResourceObjectsTable
          queryResult={queryResult}
          onClickItem={onClickItem}
          fieldTypes={mergedFieldTypes}
          sortingState={sortingState}
        />
      )}
      <AddFieldFilterModal
        opened={modalOpened}
        onClose={close}
        fieldTypes={mergedFieldTypes}
        onAdd={(record) => {
          updateSearch({
            fieldFilter: getUpdatedFilterDict(
              record.type,
              record.value,
              search.fieldFilter
            ),
          })
        }}
        values={search.fieldFilter}
      />
      <CreateResourceObjectsModal
        opened={createObjectsModalOpened}
        onClose={closeCreateObjects}
        category={category}
      />
    </Page>
  )
}
